"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { ClipboardList, PencilLine } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { ApiError } from "@/lib/api";
import { useMyRegistrationAnswers, useUpdateMyRegistrationAnswers } from "../hooks";
import { contestErrorKey } from "../lib/format";
import { draftFromAnswers, type RegistrationAnswerInput, type RegistrationQuestion } from "../registration";
import type { PublicContestDetail } from "../types";
import { RegistrationAnswersForm } from "./RegistrationAnswersForm";

/**
 * The door's second step - `EX-REG01`: the organizer's questions in a dialog,
 * after the rules are accepted and before the row is written.
 */
export function RegistrationQuestionsDialog({
  open,
  onClose,
  slug,
  title,
  questions,
  pending,
  error,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  slug: string;
  title: string;
  questions: readonly RegistrationQuestion[];
  pending: boolean;
  error: string | null;
  onSubmit: (answers: RegistrationAnswerInput[]) => void;
}) {
  const t = useTranslations("contest.page.registrationQuestions");
  return (
    <Dialog open={open} onClose={onClose} title={title} description={t("step")} closeLabel={t("close")}>
      <RegistrationAnswersForm
        slug={slug}
        questions={questions}
        submitLabel={t("finish")}
        pending={pending}
        error={error}
        onSubmit={onSubmit}
      />
    </Dialog>
  );
}

/**
 * A registered contestant and a question the organizer added after they
 * registered (ADR-0175 decision 2): asked here, on the contest page, until
 * the start. Their place never depends on it. An answered contestant may
 * still change their answers until the start.
 */
export function LaterRegistrationQuestions({ contest }: { contest: PublicContestDetail }) {
  const state = contest.viewer.registration;
  const questions = contest.registrationQuestions ?? [];
  // Nothing is read, and no hook mounts, for a reader the server asked nothing of.
  if (!state || questions.length === 0 || !state.editable) return null;
  return (
    <LaterQuestionsPrompt slug={contest.slug} questions={questions} missingCount={state.missingRequired.length} />
  );
}

function LaterQuestionsPrompt({
  slug,
  questions,
  missingCount,
}: {
  slug: string;
  questions: readonly RegistrationQuestion[];
  missingCount: number;
}) {
  const t = useTranslations("contest.page.registrationQuestions");
  const tErrors = useTranslations("contest.errors");
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mine = useMyRegistrationAnswers(slug, open);
  const update = useUpdateMyRegistrationAnswers(slug);
  const missing = missingCount > 0;

  const submit = async (answers: RegistrationAnswerInput[]) => {
    setError(null);
    try {
      await update.mutateAsync(answers);
      setOpen(false);
    } catch (err) {
      const key = contestErrorKey(err);
      setError(key ? tErrors(key) : err instanceof ApiError ? err.message : t("saveFailed"));
    }
  };

  return (
    <>
      {missing ? (
        <div
          data-testid="later-questions"
          className="flex flex-wrap items-start gap-3 rounded-[10px] border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-sm text-amber-100"
        >
          <ClipboardList className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <p className="min-w-0 flex-1 leading-relaxed">{t("laterBody", { count: missingCount })}</p>
          <Button type="button" variant="inverse" size="sm" onClick={() => setOpen(true)}>
            {t("laterAnswer")}
          </Button>
        </div>
      ) : (
        <Button type="button" variant="linkDark" size="sm" onClick={() => setOpen(true)} data-testid="edit-answers">
          <PencilLine className="h-3.5 w-3.5" aria-hidden />
          {t("editAnswers")}
        </Button>
      )}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title={missing ? t("laterTitle") : t("editTitle")}
        description={t("editLead")}
        closeLabel={t("close")}
      >
        {mine.isPending ? (
          <Skeleton className="h-48 w-full rounded-xl" />
        ) : (
          <RegistrationAnswersForm
            key={mine.dataUpdatedAt}
            slug={slug}
            questions={questions}
            initial={draftFromAnswers(mine.data?.answers ?? [])}
            submitLabel={t("saveAnswers")}
            pending={update.isPending}
            error={error}
            onSubmit={(answers) => void submit(answers)}
          />
        )}
      </Dialog>
    </>
  );
}
